import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import { StateSelect } from "@/components/ui/state-select";
import { PhoneField } from "@/components/PhoneField";
import { MaskedInput } from "@/components/ui/masked-input";
import { useToast } from "@/hooks/use-toast";
import { parseApiError } from "@/lib/api-error";
import { toastApiError, userErrorMessage } from "@/lib/error-messages";
import {
  Pencil,
  RefreshCw,
  Package,
  Building2,
  Users,
  ChevronDown,
  ChevronUp,
  Calendar,
  Hash,
  Phone,
  MapPin,
  UserPlus,
  Filter,
  Search,
  X,
} from "lucide-react";
import { getContractors } from "@/requests/internal-contractors/get-contractors";
import { updateContractor } from "@/requests/internal-contractors/update-contractor";
import { triggerOnboarding } from "@/requests/internal-contractors/onboarding";
import type { ContractorInfo, UpdateContractorRequest } from "@onsemble/shared/types/internal";
import { AddUserForm } from "./AddUserForm";

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

interface EditFormData {
  hajocaCustomerId: string;
  hajocaProfitCenter: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

function useContractorActions(contractor: ContractorInfo) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [formData, setFormData] = useState<EditFormData>({
    hajocaCustomerId: contractor.metadata.hajocaCustomerId || "",
    hajocaProfitCenter: contractor.metadata.hajocaProfitCenter || "",
    phone: contractor.metadata.phone || "",
    street: contractor.metadata.address?.street || "",
    city: contractor.metadata.address?.city || "",
    state: contractor.metadata.address?.state || "",
    zipCode: contractor.metadata.address?.zipCode || "",
    country: contractor.metadata.address?.country || "US",
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: (data: UpdateContractorRequest) =>
      updateContractor(contractor.id, data),
    onSuccess: (result) => {
      toast({
        title: "Contractor updated",
        description: `${result.orgName} has been updated successfully`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/internal/contractors"] });
      setIsEditing(false);
    },
    onError: (error) => {
      toastApiError(toast, error, "Failed to update contractor");
    },
  });

  const onboardingMutation = useMutation({
    mutationFn: () => triggerOnboarding(contractor.id),
    onSuccess: () => {
      toast({
        title: "Sync started",
        description: `Pricing and recent products sync for ${contractor.name} has been started. This may take a few minutes.`,
      });
    },
    onError: (error) => {
      toastApiError(toast, error, "Failed to start pricing sync");
    },
  });

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  const openEdit = () => {
    setFormData({
      hajocaCustomerId: contractor.metadata.hajocaCustomerId || "",
      hajocaProfitCenter: contractor.metadata.hajocaProfitCenter || "",
      phone: contractor.metadata.phone || "",
      street: contractor.metadata.address?.street || "",
      city: contractor.metadata.address?.city || "",
      state: contractor.metadata.address?.state || "",
      zipCode: contractor.metadata.address?.zipCode || "",
      country: contractor.metadata.address?.country || "US",
    });
    setIsEditing(true);
  };

  const handleSave = () => {
    const updates: UpdateContractorRequest = {
      hajocaCustomerId: formData.hajocaCustomerId || undefined,
      hajocaProfitCenter: formData.hajocaProfitCenter || undefined,
      phone: formData.phone || null,
      address: {
        street: formData.street || null,
        city: formData.city || null,
        state: formData.state || null,
        zipCode: formData.zipCode || null,
        country: formData.country || "US",
      },
    };
    updateMutation.mutate(updates);
  };

  const hasAddress = contractor.metadata.address && (
    contractor.metadata.address.street ||
    contractor.metadata.address.city ||
    contractor.metadata.address.state ||
    contractor.metadata.address.zipCode
  );

  const formatAddress = () => {
    if (!contractor.metadata.address) return null;
    const parts = [
      contractor.metadata.address.street,
      contractor.metadata.address.city,
      contractor.metadata.address.state,
      contractor.metadata.address.zipCode,
    ].filter(Boolean);
    return parts.join(", ");
  };

  const canSync = !!(contractor.metadata.hajocaCustomerId && contractor.metadata.hajocaProfitCenter);

  return {
    isExpanded,
    isEditing,
    setIsEditing,
    isAddingUser,
    setIsAddingUser,
    formData,
    setFormData,
    updateMutation,
    onboardingMutation,
    toggleExpanded,
    openEdit,
    handleSave,
    hasAddress,
    formatAddress,
    canSync,
  };
}

function ContractorDialogs({
  contractor,
  actions,
}: {
  contractor: ContractorInfo;
  actions: ReturnType<typeof useContractorActions>;
}) {
  return (
    <>
      <Dialog open={actions.isEditing} onOpenChange={actions.setIsEditing}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Contractor</DialogTitle>
            <DialogDescription>
              Update metadata for {contractor.name}. Changes will be saved to both Clerk and Supabase.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="hajocaCustomerId">Customer ID</Label>
                <Input
                  id="hajocaCustomerId"
                  value={actions.formData.hajocaCustomerId}
                  onChange={(e) => actions.setFormData({ ...actions.formData, hajocaCustomerId: e.target.value })}
                  placeholder="W178156"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hajocaProfitCenter">Profit Center</Label>
                <Input
                  id="hajocaProfitCenter"
                  value={actions.formData.hajocaProfitCenter}
                  onChange={(e) => actions.setFormData({ ...actions.formData, hajocaProfitCenter: e.target.value })}
                  placeholder="125"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <PhoneField
                value={actions.formData.phone}
                onChange={(value) => actions.setFormData({ ...actions.formData, phone: value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="street">Street Address</Label>
              <Input
                id="street"
                value={actions.formData.street}
                onChange={(e) => actions.setFormData({ ...actions.formData, street: e.target.value })}
                placeholder="123 Main St"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={actions.formData.city}
                  onChange={(e) => actions.setFormData({ ...actions.formData, city: e.target.value })}
                  placeholder="Antioch"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <StateSelect
                  value={actions.formData.state}
                  onValueChange={(value) => actions.setFormData({ ...actions.formData, state: value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="zipCode">ZIP Code</Label>
              <MaskedInput
                id="zipCode"
                mask="zipcode"
                value={actions.formData.zipCode}
                onChange={(e) => actions.setFormData({ ...actions.formData, zipCode: e.target.value })}
                placeholder="94509"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => actions.setIsEditing(false)}>
              Cancel
            </Button>
            <Button
              onClick={actions.handleSave}
              disabled={actions.updateMutation.isPending}
            >
              {actions.updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={actions.isAddingUser} onOpenChange={actions.setIsAddingUser}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add User to {contractor.name}</DialogTitle>
            <DialogDescription>
              Create a new user and add them to this contractor organization.
            </DialogDescription>
          </DialogHeader>
          <AddUserForm
            orgId={contractor.id}
            orgName={contractor.name}
            onSuccess={() => actions.setIsAddingUser(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

function ContractorCard({ contractor }: { contractor: ContractorInfo }) {
  const actions = useContractorActions(contractor);

  return (
    <>
      <div className="rounded-lg border bg-card p-4 space-y-3">
        {/* Header: avatar + name + chevron */}
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={actions.toggleExpanded}
        >
          {contractor.imageUrl ? (
            <img
              src={contractor.imageUrl}
              alt={contractor.name}
              className="h-10 w-10 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
              <Building2 className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{contractor.name}</div>
            <div className="text-xs text-muted-foreground font-mono">
              PC: {contractor.metadata.hajocaProfitCenter || "—"}
            </div>
          </div>
          {actions.isExpanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          )}
        </div>

        {/* Actions row */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => actions.openEdit()}
          >
            <Pencil className="h-3 w-3 mr-1" />
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => actions.setIsAddingUser(true)}
          >
            <UserPlus className="h-3 w-3 mr-1" />
            Add User
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => actions.onboardingMutation.mutate()}
            disabled={!actions.canSync || actions.onboardingMutation.isPending}
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${actions.onboardingMutation.isPending ? "animate-spin" : ""}`} />
            {actions.onboardingMutation.isPending ? "Syncing..." : "Sync"}
          </Button>
        </div>

        {/* Expanded details */}
        {actions.isExpanded && (
          <div className="border-t pt-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Customer ID
                </div>
                <div className="text-sm font-mono">
                  {contractor.metadata.hajocaCustomerId || <span className="text-muted-foreground">—</span>}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                  <Package className="h-3 w-3" />
                  Products
                </div>
                <div className="text-sm">{contractor.productPricingCount.toLocaleString()}</div>
              </div>
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  Members
                </div>
                <div className="text-sm">{contractor.membersCount}</div>
              </div>
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Created
                </div>
                <div className="text-sm">{formatDate(contractor.createdAt)}</div>
              </div>
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                  <Hash className="h-3 w-3" />
                  Slug
                </div>
                <div className="text-sm font-mono">
                  {contractor.slug || <span className="text-muted-foreground">—</span>}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  Phone
                </div>
                <div className="text-sm">
                  {contractor.metadata.phone || <span className="text-muted-foreground">—</span>}
                </div>
              </div>
              {actions.hasAddress && (
                <div className="space-y-1 col-span-2">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    Address
                  </div>
                  <div className="text-sm">{actions.formatAddress()}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <ContractorDialogs contractor={contractor} actions={actions} />
    </>
  );
}

function ContractorRow({ contractor }: { contractor: ContractorInfo }) {
  const actions = useContractorActions(contractor);

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={actions.toggleExpanded}
      >
        <TableCell className="w-10">
          {actions.isExpanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-3">
            {contractor.imageUrl ? (
              <img
                src={contractor.imageUrl}
                alt={contractor.name}
                className="h-8 w-8 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                <Building2 className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            <div className="min-w-0">
              <div className="font-medium truncate">{contractor.name}</div>
              <div className="text-xs text-muted-foreground font-mono truncate">
                {contractor.id}
              </div>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <span className="font-mono text-sm">
            {contractor.metadata.hajocaCustomerId || (
              <span className="text-muted-foreground">—</span>
            )}
          </span>
        </TableCell>
        <TableCell>
          <span className="font-mono text-sm">
            {contractor.metadata.hajocaProfitCenter || (
              <span className="text-muted-foreground">—</span>
            )}
          </span>
        </TableCell>
        <TableCell>
          <Badge variant="secondary" className="font-mono">
            <Package className="h-3 w-3 mr-1" />
            {contractor.productPricingCount.toLocaleString()}
          </Badge>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <Users className="h-3 w-3 text-muted-foreground" />
            <span className="text-sm">{contractor.membersCount}</span>
          </div>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                actions.openEdit();
              }}
            >
              <Pencil className="h-3 w-3 mr-1" />
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                actions.setIsAddingUser(true);
              }}
            >
              <UserPlus className="h-3 w-3 mr-1" />
              Add User
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                actions.onboardingMutation.mutate();
              }}
              disabled={!actions.canSync || actions.onboardingMutation.isPending}
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${actions.onboardingMutation.isPending ? "animate-spin" : ""}`} />
              {actions.onboardingMutation.isPending ? "Syncing..." : "Sync"}
            </Button>
          </div>
        </TableCell>
      </TableRow>

      {actions.isExpanded && (
        <TableRow className="bg-muted/30 hover:bg-muted/30">
          <TableCell colSpan={7}>
            <div className="py-4 px-2">
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-1">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Created
                  </div>
                  <div className="text-sm">{formatDate(contractor.createdAt)}</div>
                </div>
                <div className="space-y-1">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                    <Hash className="h-3 w-3" />
                    Slug
                  </div>
                  <div className="text-sm font-mono">
                    {contractor.slug || <span className="text-muted-foreground">—</span>}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    Phone
                  </div>
                  <div className="text-sm">
                    {contractor.metadata.phone || <span className="text-muted-foreground">—</span>}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    Address
                  </div>
                  <div className="text-sm">
                    {actions.hasAddress ? actions.formatAddress() : <span className="text-muted-foreground">—</span>}
                  </div>
                </div>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}

      <ContractorDialogs contractor={contractor} actions={actions} />
    </>
  );
}

const PAGE_SIZE = 20;

function getPageNumbers(currentPage: number, totalPages: number): (number | "ellipsis")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | "ellipsis")[] = [1];

  if (currentPage > 3) {
    pages.push("ellipsis");
  }

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (currentPage < totalPages - 2) {
    pages.push("ellipsis");
  }

  pages.push(totalPages);

  return pages;
}

type SearchMode = "name" | "id";

export function ContractorsTable() {
  const [profitCenterFilter, setProfitCenterFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchMode, setSearchMode] = useState<SearchMode>("name");
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [activeMode, setActiveMode] = useState<SearchMode>("name");

  const { data, isLoading, error } = useQuery({
    queryKey: ["/api/internal/contractors", activeMode, activeSearch],
    queryFn: () =>
      getContractors(
        !activeSearch
          ? {}
          : activeMode === "id"
            ? { hajocaCustomerId: activeSearch }
            : { q: activeSearch }
      ),
  });

  const contractors = data?.data || [];
  const totalCount = data?.pagination?.total ?? contractors.length;

  const submitSearch = () => {
    setActiveSearch(searchInput.trim());
    setActiveMode(searchMode);
    setCurrentPage(1);
  };
  const clearSearch = () => {
    setSearchInput("");
    setActiveSearch("");
    setCurrentPage(1);
  };

  const profitCenters = useMemo(() => {
    const pcs = new Set<string>();
    for (const c of contractors) {
      if (c.metadata.hajocaProfitCenter) {
        pcs.add(c.metadata.hajocaProfitCenter);
      }
    }
    return Array.from(pcs).sort();
  }, [contractors]);

  const filtered = useMemo(() => {
    if (profitCenterFilter === "all") return contractors;
    return contractors.filter(
      (c) => c.metadata.hajocaProfitCenter === profitCenterFilter
    );
  }, [contractors, profitCenterFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paged = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  const handleFilterChange = (value: string) => {
    setProfitCenterFilter(value);
    setCurrentPage(1);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        Failed to load contractors: {userErrorMessage(parseApiError(error))}
      </div>
    );
  }

  if (contractors.length === 0 && !activeSearch) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No contractors found. Add your first contractor to get started.
      </div>
    );
  }

  const rangeStart = (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, filtered.length);

  return (
    <div className="flex flex-col gap-4">
      {/* Control bar: search + filter + pagination */}
      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={searchMode}
          onValueChange={(v) => setSearchMode(v as SearchMode)}
        >
          <SelectTrigger className="w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="id">Customer ID</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative w-[220px] md:w-[280px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitSearch();
            }}
            placeholder={
              searchMode === "id"
                ? "Exact customer ID (e.g. C1234, W163826)"
                : "Search by name"
            }
            className="pl-8 pr-8"
          />
          {searchInput && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button variant="secondary" size="sm" onClick={submitSearch}>
          Search
        </Button>
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select value={profitCenterFilter} onValueChange={handleFilterChange}>
          <SelectTrigger className="w-[180px] md:w-[200px]">
            <SelectValue placeholder="Profit Center" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Profit Centers</SelectItem>
            {profitCenters.map((pc) => (
              <SelectItem key={pc} value={pc}>
                {pc}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {activeSearch
            ? `${filtered.length} result${filtered.length === 1 ? "" : "s"} for ${activeMode === "id" ? "customer ID" : "name"} "${activeSearch}"`
            : filtered.length === contractors.length
              ? `${contractors.length} of ${totalCount} contractors`
              : `${filtered.length} of ${contractors.length} loaded (${totalCount} total)`}
        </span>
        {totalPages > 1 && (
          <>
            <div className="hidden sm:block flex-1" />
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {rangeStart}–{rangeEnd} of {filtered.length}
              </span>
              <Pagination className="mx-0 w-auto">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      className={safePage <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                  {getPageNumbers(safePage, totalPages).map((page, i) =>
                    page === "ellipsis" ? (
                      <PaginationItem key={`ellipsis-${i}`}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    ) : (
                      <PaginationItem key={page}>
                        <PaginationLink
                          isActive={page === safePage}
                          onClick={() => setCurrentPage(page)}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  )}
                  <PaginationItem>
                    <PaginationNext
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      className={safePage >= totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          </>
        )}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          No contractors match{" "}
          {activeSearch
            ? `${activeMode === "id" ? "customer ID" : "name"} "${activeSearch}"`
            : "the selected filter"}
          .
        </div>
      )}

      {/* Scrollable content area */}
      {/* Mobile: card list */}
      <div className="md:hidden overflow-y-auto max-h-[calc(100vh-280px)] space-y-3 -mx-1 px-1">
        {paged.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No contractors match the selected filter.
          </div>
        ) : (
          paged.map((contractor) => (
            <ContractorCard key={contractor.id} contractor={contractor} />
          ))
        )}
      </div>

      {/* Desktop: table */}
      <div className="hidden md:block rounded-md border overflow-y-auto max-h-[calc(100vh-280px)]">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead className="min-w-[200px]">Organization</TableHead>
              <TableHead>Customer ID</TableHead>
              <TableHead>Profit Center</TableHead>
              <TableHead>Products</TableHead>
              <TableHead>Members</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No contractors match the selected filter.
                </TableCell>
              </TableRow>
            ) : (
              paged.map((contractor) => (
                <ContractorRow key={contractor.id} contractor={contractor} />
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
